import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useQuoteComparison from '../../hooks/useQuoteComparison';
import ScoreBreakdown from './ScoreBreakdown';
import EvidenceViewer from './EvidenceViewer';

const QuoteComparison = () => {
  const { id: shipmentId, comparisonId } = useParams();
  const navigate = useNavigate();
  const {
    comparison,
    quotes,
    loading,
    error,
    expandedQuote,
    evidenceModal,
    fetchComparison,
    selectQuote,
    autoSelect,
    confirmBooking,
    toggleBreakdown,
    viewEvidence,
    closeEvidence
  } = useQuoteComparison();

  useEffect(() => {
    if (shipmentId) {
      fetchComparison(shipmentId, comparisonId);
    }
  }, [shipmentId, comparisonId]);

  if (loading) {
    return <div className="loading">Loading quote comparison...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h2>Error Loading Comparison</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!comparison) {
    return null;
  }

  const { scores, recommendedQuoteId, reasoning, autoSelectAllowed, autoSelectReason, manualReviewReasons } = comparison;

  const handleAutoSelect = async () => {
    if (!window.confirm('Confirm auto-selection of recommended quote?')) {
      return;
    }

    const success = await autoSelect();
    if (success) {
      const confirmBookingNow = window.confirm(
        'Quote selected! Would you like to confirm booking now?'
      );
      if (confirmBookingNow) {
        await confirmBooking(recommendedQuoteId);
      }
      navigate(`/shipments/${shipmentId}`);
    }
  };

  const handleManualSelect = async (quoteId) => {
    const reason = window.prompt('Please provide a reason for selecting this quote:');
    if (!reason) {
      return;
    }

    const success = await selectQuote(quoteId, reason);
    if (success) {
      const confirmBookingNow = window.confirm(
        'Quote selected! Would you like to confirm booking now?'
      );
      if (confirmBookingNow) {
        await confirmBooking(quoteId);
      }
      navigate(`/shipments/${shipmentId}`);
    }
  };

  const getQuoteById = (quoteId) => {
    return quotes.find((q) => q.id === quoteId);
  };

  const formatCost = (cost) => {
    return `$${cost?.toLocaleString() || 'N/A'}`;
  };

  const formatDays = (days) => {
    return days ? `${days} days` : 'TBD';
  };

  return (
    <div className="comparison-container">
      <div className="page-header">
        <h1>Quote Comparison</h1>
        <button onClick={() => navigate(-1)} className="btn-secondary">
          Back
        </button>
      </div>

      {/* Auto-Select Banner */}
      {autoSelectAllowed && (
        <div className="recommendation-banner">
          <div className="banner-content">
            <div className="banner-icon">✓</div>
            <div className="banner-text">
              <h3>Auto-Selection Available</h3>
              <p>{autoSelectReason}</p>
              <p className="reasoning">{reasoning}</p>
            </div>
          </div>
          <button onClick={handleAutoSelect} className="btn-primary btn-large">
            Auto-Select & Continue
          </button>
        </div>
      )}

      {/* Manual Review Warning */}
      {!autoSelectAllowed && manualReviewReasons && manualReviewReasons.length > 0 && (
        <div className="warning-banner">
          <div className="banner-content">
            <div className="banner-icon">⚠</div>
            <div className="banner-text">
              <h3>Manual Review Required</h3>
              <ul>
                {manualReviewReasons.map((reason, idx) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
              <p className="reasoning">{reasoning}</p>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <div className="dashboard-card">
        <h2>Quote Comparison ({scores.length} quotes)</h2>

        <div className="quote-table-wrapper">
          <table className="quote-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Provider</th>
                <th>Total Cost</th>
                <th>Transit Time</th>
                <th>Total Score</th>
                <th>Score Breakdown</th>
                <th>Evidence</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scores.map((score) => {
                const quote = getQuoteById(score.quoteId);
                const isRecommended = score.quoteId === recommendedQuoteId;
                const isExpanded = expandedQuote === score.quoteId;

                return (
                  <tr
                    key={score.quoteId}
                    className={isRecommended ? 'recommended' : ''}
                  >
                    <td>
                      <div className="rank">
                        #{score.rank}
                        {isRecommended && (
                          <span className="badge-recommended">Recommended</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="provider-cell">
                        <strong>{score.providerName}</strong>
                        <div className="provider-meta">
                          Reliability: {Math.round(score.reliabilityScore)}%
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong>{formatCost(score.totalCost)}</strong>
                    </td>
                    <td>{formatDays(score.transitDays)}</td>
                    <td>
                      <div className="score-cell">
                        <div className="score-value-large">{score.totalScore}</div>
                        <div className="score-label">/ 100</div>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => toggleBreakdown(score.quoteId)}
                        className="btn-link"
                      >
                        {isExpanded ? 'Hide Details' : 'View Details'}
                      </button>
                    </td>
                    <td>
                      {quote?.sourceDocument && (
                        <button
                          onClick={() => viewEvidence(score.quoteId)}
                          className="btn-link"
                        >
                          View Evidence
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => handleManualSelect(score.quoteId)}
                        className="btn-primary btn-sm"
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Expandable Score Breakdown */}
        {expandedQuote && (
          <div className="score-breakdown-container">
            <ScoreBreakdown
              score={scores.find((s) => s.quoteId === expandedQuote)}
              quote={getQuoteById(expandedQuote)}
              onClose={() => toggleBreakdown(null)}
            />
          </div>
        )}
      </div>

      {/* Cost Comparison Chart */}
      <div className="dashboard-card">
        <h2>Cost Comparison</h2>
        <div className="cost-chart">
          {scores.map((score) => {
            const maxCost = Math.max(...scores.map((s) => s.totalCost));
            const barWidth = (score.totalCost / maxCost) * 100;

            return (
              <div key={score.quoteId} className="cost-bar-item">
                <div className="cost-bar-label">
                  <span>{score.providerName}</span>
                  <strong>{formatCost(score.totalCost)}</strong>
                </div>
                <div className="cost-bar-track">
                  <div
                    className={`cost-bar-fill ${score.quoteId === recommendedQuoteId ? 'recommended' : ''}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Evidence Modal */}
      {evidenceModal && (
        <EvidenceViewer
          quote={getQuoteById(evidenceModal)}
          onClose={closeEvidence}
        />
      )}
    </div>
  );
};

export default QuoteComparison;
